import { Fragment, useCallback, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import ButtonComponent from '../../../components/stories/atoms/button'
import {
  buttonSizeOptions,
  buttonTypeOptions,
  buttonVariantOptions
} from '../../../components/stories/atoms/button/constants'
import { Context } from '../../../components/stories/atoms/icons/essentials'
import colorsOptions from '../../../components/stories/fundamentals/colors-styles/constants'
import ContextMenuComponent from '../../../components/stories/organisms/context-menu'
import {
  contextMenuAlignOptions,
  contextMenuArrowAlignmentOptions,
  contextMenuPaddingOptions,
  contextMenuSideOptions,
  contextMenuTitleSizeOptions,
  contextMenuTitleWeightOptions,
  contextMenuVariantOptions
} from '../../../components/stories/organisms/context-menu/constants'
import { useUpdateProjectMutation } from '../../../react-query/project/mutation/use-update-project-mutation'
import { projectTreeItemTypeOptions } from '../../../react-query/project/query/use-get-project-tree-query'
import { useIntl } from 'react-intl'
import PropTypes from 'prop-types'
import { EllipseBold } from '../../../components/stories/atoms/icons/image-overlay'
import DeactivateProjectAlert from './deactivate-project-alert'
import DeleteFolderAlert from '../../../components/delete-folder-alert'
import { useSnackbar } from '../../../components/stories/molecules/snackbar/component/snackbar-hook'
import { snackbarVariantOptions } from '../../../components/stories/molecules/snackbar/constants'
import { useDeleteFolderMutation } from '../../../react-query/project/mutation/use-delete-folder'

const canUsePermission = (permissions, permission) =>
  permissions?.includes(permission)

const getContextMenuItems = ({
  formatMessage,
  item,
  userPermissions,

  // project handlers
  onCopyProject,
  onDeactivateItem,
  onMoveItem,
  onToggleProjectActive,

  // folder handlers
  onCreateProject,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder
}) => {
  const isActive = item.active
  const isProject = item.type === projectTreeItemTypeOptions.PROJECT
  const canMove =
    isActive && canUsePermission(userPermissions, 'CreateProjectFolders')
  const canCreateProject = canUsePermission(userPermissions, 'CreateProjects')
  const canCreateFolder = canUsePermission(
    userPermissions,
    'CreateProjectFolders'
  )
  const canCopyProject = isActive && isProject && canCreateProject
  const canToggleProjectActive = isProject && canCreateProject

  return [
    {
      id: 'new-project',
      title: formatMessage({ id: 'projects_createProject' }),
      action: () => onCreateProject(item),
      hide: !canCreateProject || isProject || !onCreateProject,
      showDivider: false
    },
    {
      id: 'new-folder',
      title: formatMessage({ id: 'projects_newFolder' }),
      action: () => onCreateFolder(item),
      hide: !canCreateFolder || isProject || !onCreateFolder,
      showDivider: true
    },
    {
      id: 'move',
      title: formatMessage({ id: 'move' }),
      action: () => onMoveItem(item),
      hide: !canMove || !onMoveItem,
      showDivider: false
    },
    {
      id: 'rename',
      title: formatMessage({ id: 'rename' }),
      action: () => onRenameFolder(item),
      hide: !canCreateFolder || isProject || !onRenameFolder,
      showDivider: true
    },
    {
      id: 'delete',
      title: formatMessage({ id: 'delete' }),
      action: () => onDeleteFolder(item),
      hide: !canCreateFolder || isProject || !onDeleteFolder,
      disabled: item.items > 0,
      variant: contextMenuVariantOptions.DESTRUCTIVE,
      showDivider: false
    },
    {
      id: 'copy',
      title: formatMessage({
        id: 'projects_copyProject'
      }),
      action: () => onCopyProject(item),
      hide: !canCopyProject || !onCopyProject || !isProject,
      showDivider: true
    },
    {
      id: 'project-active-toggle',
      title: formatMessage({
        id: isActive ? 'deactivate' : 'activate'
      }),
      action: () =>
        onDeactivateItem ? onDeactivateItem(item) : onToggleProjectActive(item),
      hide:
        !canToggleProjectActive ||
        (!onDeactivateItem && !onToggleProjectActive) ||
        !isProject,
      variant: isActive
        ? contextMenuVariantOptions.DESTRUCTIVE
        : contextMenuVariantOptions.STANDARD,
      showDivider: false
    }
  ].filter(({ hide }) => !hide)
}

const CompanyProjectsContextMenu = ({
  item,
  userPermissions = [],
  isTileView = false,

  // project handlers
  onCopyProject,
  onDeactivateItem,
  onMoveItem,

  // folder handlers
  onCreateProject,
  onCreateFolder,
  onRenameFolder
}) => {
  const { formatMessage } = useIntl()
  const [isOpen, setIsOpen] = useState(false)
  const [projectToDeactivate, setProjectToDeactivate] = useState(null)
  const [isDeleteFolderAlertOpen, setIsDeleteFolderAlertOpen] = useState(false)
  const { addSnackbar } = useSnackbar()

  const iconColor = item.active
    ? colorsOptions.greys['grey-90']
    : colorsOptions.greys['grey-40']

  const showSnackbarFolderDeletedSuccess = () => {
    addSnackbar({
      message: formatMessage({ id: 'folder_deleted' }, { count: 1 }),
      snackbarType: snackbarVariantOptions.SUCCESS,
      hideCloseButton: true
    })
  }

  const showSnackbarProjectToggleIsActiveSuccess = (data) => {
    const { active } = data

    addSnackbar({
      message: formatMessage(
        {
          id: active ? 'project_reactivated' : 'project_deactivated'
        },
        { count: 1 }
      ),
      snackbarType: snackbarVariantOptions.SUCCESS,
      hideCloseButton: true
    })
  }

  // mutates

  const { mutate: deleteFolder, isPending: deleteFolderLoading } =
    useDeleteFolderMutation({
      onSuccess: () => {
        setIsDeleteFolderAlertOpen(false)
        showSnackbarFolderDeletedSuccess()
      }
    })

  const { mutate: updateProject, isPending: updateProjectLoading } =
    useUpdateProjectMutation({
      onSuccess: showSnackbarProjectToggleIsActiveSuccess
    })

  // handlers

  const handleUpdateProjectActive = useCallback(
    (project, active) => {
      updateProject({
        projectId: project.id,
        body: { active }
      })
    },
    [updateProject]
  )

  const handleToggleProjectActive = useCallback(
    (project) => {
      if (project.active) {
        setProjectToDeactivate(project)
        return
      }

      handleUpdateProjectActive(project, true)
    },
    [handleUpdateProjectActive]
  )

  const handleSetProjectInactive = (e) => {
    e.stopPropagation()
    handleUpdateProjectActive(projectToDeactivate, false)
  }

  const handleTriggerClick = useCallback((event) => {
    event.stopPropagation()
  }, [])

  const handleItemClick = (action) => (event) => {
    event.stopPropagation()
    setIsOpen(false)
    action()
  }

  const handleCloseInactiveAlert = (e) => {
    e.stopPropagation()
    setProjectToDeactivate(null)
  }

  const handleOpenDeleteFolderAlert = () => {
    setIsDeleteFolderAlertOpen(true)
  }

  const handleDeleteFolder = useCallback(() => {
    deleteFolder({ folderIdList: [item.id] })
  }, [deleteFolder, item.id])

  const menuItems = useMemo(
    () =>
      getContextMenuItems({
        formatMessage,
        item,
        userPermissions,
        // project handlers
        onCopyProject,
        onDeactivateItem,
        onMoveItem,
        onToggleProjectActive: handleToggleProjectActive,

        // folder handlers
        onCreateProject,
        onCreateFolder,
        onRenameFolder,
        onDeleteFolder: handleOpenDeleteFolderAlert
      }),
    [
      formatMessage,
      item,
      userPermissions,

      // project handlers
      onCopyProject,
      onDeactivateItem,
      handleToggleProjectActive,
      onMoveItem,

      // folder handlers
      onCreateProject,
      onCreateFolder,
      onRenameFolder,
      handleOpenDeleteFolderAlert
    ]
  )

  const tableTriggerButton = (
    <ButtonComponent
      variant={buttonVariantOptions.GHOST}
      buttonType={buttonTypeOptions['ICON-ONLY']}
      disabled={!menuItems.length}
    >
      <Context
        style={{
          fill: iconColor,
          stroke: iconColor,
          strokeWidth: 0
        }}
      />
    </ButtonComponent>
  )

  const tilesTriggerButton = (
    <ButtonComponent
      buttonType={buttonTypeOptions['ICON-ONLY']}
      variant={buttonVariantOptions['IMAGE-OVERLAY']}
      size={buttonSizeOptions.SMALL}
    >
      <EllipseBold stroke={colorsOptions.greys.white} />
    </ButtonComponent>
  )

  const triggerRef = useRef(null)
  const defineTrigger = isTileView ? tilesTriggerButton : tableTriggerButton

  if (!menuItems.length) {
    return defineTrigger
  }

  const triggerRect =
    isOpen && triggerRef.current
      ? triggerRef.current.getBoundingClientRect()
      : null

  // Check if the context menu overlaps the bottom limit of the screen
  const MENU_ESTIMATED_HEIGHT = 250
  const openUpward = triggerRect
    ? window.innerHeight - triggerRect.bottom < MENU_ESTIMATED_HEIGHT
    : false

  return (
    <>
      <div ref={triggerRef}>
        <ContextMenuComponent.Root isOpen={isOpen} setIsOpen={setIsOpen}>
          <ContextMenuComponent.Trigger callback={handleTriggerClick}>
            {defineTrigger}
          </ContextMenuComponent.Trigger>
          {triggerRect &&
            createPortal(
              <div onMouseDown={(e) => e.stopPropagation()}>
                <ContextMenuComponent.List
                  width="200px"
                  align={contextMenuAlignOptions.START}
                  side={
                    openUpward
                      ? contextMenuSideOptions.TOP
                      : contextMenuSideOptions.BOTTOM
                  }
                  arrow
                  arrowAlignment={contextMenuArrowAlignmentOptions.END}
                  style={{
                    position: 'fixed',
                    top: openUpward ? 'auto' : triggerRect.bottom,
                    bottom: openUpward
                      ? window.innerHeight - triggerRect.top
                      : 'auto',
                    right: window.innerWidth - triggerRect.right,
                    left: 'auto',
                    transform: 'none',
                    zIndex: 9999
                  }}
                >
                  {menuItems.map(
                    ({
                      action,
                      id,
                      title,
                      variant,
                      showDivider = false,
                      disabled
                    }) => (
                      <Fragment key={id}>
                        <ContextMenuComponent.Item
                          id={id}
                          variant={variant}
                          padding={contextMenuPaddingOptions['EXTRA-LARGE']}
                          titleWeight={
                            contextMenuTitleWeightOptions['TEXT-600']
                          }
                          titleSize={contextMenuTitleSizeOptions['TEXT-R']}
                          onClick={handleItemClick(action)}
                          title={title}
                          disabled={disabled}
                        />
                        {showDivider && <ContextMenuComponent.Divider />}
                      </Fragment>
                    )
                  )}
                </ContextMenuComponent.List>
              </div>,
              document.body
            )}
        </ContextMenuComponent.Root>
      </div>

      {projectToDeactivate && (
        <DeactivateProjectAlert
          onClose={handleCloseInactiveAlert}
          onConfirm={handleSetProjectInactive}
          isLoading={updateProjectLoading}
        />
      )}

      <DeleteFolderAlert
        isOpen={isDeleteFolderAlertOpen}
        onClose={() => setIsDeleteFolderAlertOpen(false)}
        onDelete={handleDeleteFolder}
        onDeleteLoading={deleteFolderLoading}
      />
    </>
  )
}

CompanyProjectsContextMenu.propTypes = {
  item: PropTypes.object,
  onCopyProject: PropTypes.func,
  onDeactivateItem: PropTypes.func,
  onMoveItem: PropTypes.func,
  userPermissions: PropTypes.array,
  isTileView: PropTypes.bool
}

export default CompanyProjectsContextMenu
